<template>
  <AppSection
    id="personal-contacts"
    :title="this.$getConst.pages.personalDetails.personal_contacts_title"
  >
    <b-row>
      <b-col xl="6" lg="8">
        <a v-if="isSmsEnabled" role="button" @click="onHaveCode" class="float-right font-weight-bold">{{$t($getConst.pages.personalDetails.have_verification_code) | capitalize}}?</a>
        <b-form-group id="phone" :label="$t($getConst.pages.personalDetails.phone_number)" label-for="phone-input">
          <b-input-group class="mt-3">
            <VuePhoneNumberInput
              id="phone-input"
              v-model="$v.phoneBasicNumber.$model"
              :only-countries="allowedCountries"
              :loader="isLoadingCountries"
              class="text-capitalize flex-grow-1"
              :class="{ 'is-invalid': $v.form.phone.$error }"
              :placeholder="$t(this.$getConst.pages.personalDetails.phone_number)"
              :border-radius="8"
              :translations="translation"
              :clearable="true"
              :disabled="isUpdatingSms || isLoadingCountries"
              no-use-browser-locale
              size="lg"
              error-color	= "#e55353"
              valid-color	= "#0051f6"
              color	= "#0051f6"
              required
              ref="PhoneNumberInput"
              @update="showPayload"
            />
            <b-input-group-append style="z-index: 0 !important;" v-if="isSmsEnabled">
              <b-button
                v-if="isPhoneVerified && !isDataChanged"
                disabled
                variant="success"
              >
                {{$t($getConst.pages.sign_up.phone_verified) | capitalize}}<i :class="[$getConst.icons.check, 'ml-1']"/>
              </b-button>
              <b-button
                v-else
                variant="secondary"
                :disabled="!isDataChanged || $v.form.phone.$error || isUpdatingSms"
                @click="sendVerificationCode"
              >
                {{$t($getConst.pages.personalDetails.verify_your_phone) | capitalize}}
              </b-button>
            </b-input-group-append>
            <b-input-group-append style="z-index: 0 !important;" v-else>

            <b-button
                variant="secondary"
                @click="updatePhoneNumber"
                :disabled = "isLoading || !isDataChanged"
              >
              <b-spinner small variant="white" v-if="isLoading"/>

              <template v-if="!isFreelancer()">
                {{$t($getConst.pages.button.save) | capitalize}}
              </template>

              <template v-if="isFreelancer() && !isLoading">
                <i :class="$getConst.icons.save + ' fa-lg'" />
              </template>

              </b-button>

            </b-input-group-append>
          </b-input-group>

          <AppFormFeedback :errors="$v.form.phone"/>
          <b-form-invalid-feedback v-if="!$v.form.phone.validPhoneNumber && $v.form.phone.required">
            {{$t(this.$getConst.pages.form.phone_field_error)}}.
          </b-form-invalid-feedback>
        </b-form-group>
        <div v-if="isSmsEnabled">
          <b-form-checkbox
            v-if="!isUpdatingSms"
            id="sms"
            v-model="form.sms"
            :disabled="!isPhoneVerified || isDataChanged || isUpdatingSms"
            class="mb-2"
            style="z-index: 0 !important;"
            name="sms"
            :value="true"
            :unchecked-value="false"
            switch
            @change="updateSms"
          >
            {{this.$t(this.$getConst.pages.sign_up.accept_sms_text)}}.
          </b-form-checkbox>

          <label v-else><b-spinner small variant="secondary"/> {{this.$t(this.$getConst.pages.sign_up.accept_sms_text)}}.</label>
          <small v-if="!isPhoneVerified || isDataChanged" class="form-text text-muted">{{$t($getConst.pages.personalDetails.enable_sms_description) | capitalize}}</small>
        </div>

      </b-col>
    </b-row>

    <VerificationModal
      :phone="phoneToVerify"
      @close="closeModal"
    />
  </AppSection>
</template>

<script>
  import VuePhoneNumberInput from 'vue-phone-number-input';
  import AppFormFeedback from "../../../components/common/form/AppFormFeedback";
  import AppSection from "../../../components/common/sections/AppSection";
  import AppSpinner from "../../../components/common/utils/AppSpinner";

  import AppConfirmButtons from "../../../components/common/utils/AppConfirmButtons";
  import AppModal from "../../../components/common/modals/AppModal";
  import VerificationModal from "./VerificationModal";
  import {sendVerificationCode, updatePhoneWithoutVerification} from "../../../api/userPhone.api";
  import {updateSms} from "../../../api/user.api";
  import parsePhoneNumber from 'libphonenumber-js';
  import {getAllowedMobileCountries} from "../../../api/scopes.api";

  export default {
    name: "PersonalContacts",
    components: {VerificationModal, AppModal, AppConfirmButtons, AppSection, AppFormFeedback, VuePhoneNumberInput},
    data() {
      return {
        isPhoneVerified: false,
        phoneBasicNumber:null,
        userContacts: null,
        form: {
          phone: null,
          sms: null
        },
        phoneToVerify: null,
        validNumber: true,
        translation: {
          countrySelectorLabel: '',
          countrySelectorError: '',
          phoneNumberLabel: this.$t(this.$getConst.pages.personalDetails.phone_number),
          example: ''
        },
        defaultCountry: JSON.parse(localStorage.getItem("userAccount")).scopeDefault.country_code,
        isSmsEnabled: JSON.parse(localStorage.getItem("userAccount")).scopeDefault.enable_sms,
        isUpdating: false,
        isUpdatingSms: false,
        isLoadingCountries: false,
        isLoading: false,
        allowedCountries: []
      }
    },
    validations: {
      phoneBasicNumber: {validPhoneNumber() {return this.validNumber}},
      form: {
        phone: {validPhoneNumber() {return this.validNumber}},
      },
    },
    computed: {
      isDataChanged() {
        if (!this.userContacts.phone) {
          return true;
        }
        return (this.form.phone !== parsePhoneNumber(this.userContacts.phone).number);
      }
    },
    methods: {
      showPayload (payload) {
        this.$v.form.phone.$model = payload.formattedNumber;
        this.validNumber = !!payload.isValid;
      },
      onCancelChanges() {
        this.getEditableData();
      },
      getEditableData() {
        this.form = {
          phone: this.userContacts.phone,
          sms: this.userContacts.sms,
        }
        if (this.userContacts.phone) {
          this.isPhoneVerified = true;
        }
        this.phoneBasicNumber = this.userContacts.phone;
      },
      sendVerificationCode() {
        this.$emit("update", true);
        sendVerificationCode(this.form.phone).then(
          (response) => {
            this.phoneToVerify = response.data.phone;
            this.$bvModal.show('verify-phone');
            this.onCancelChanges();
            this.$emit("update", false);
          },
          (error) => {
            this.handleApiError(error);
            this.$emit("update", false);
          }
        )
      },
      onHaveCode() {
        this.phoneToVerify = null;
        this.$bvModal.show('verify-phone');
      },
      closeModal(arg) {
        if (arg) {
          this.userContacts.phone = arg.phone;
          this.userContacts.sms = arg.sendSms;
          this.form.phone = arg.phone;
          this.form.sms = arg.sendSms;
          this.phoneBasicNumber = arg.phone;
          this.isPhoneVerified = true;
        }
        this.$bvModal.hide('verify-phone');
      },
      updatePhoneNumber() {
        this.isLoading = true;
        this.error = null;
        updatePhoneWithoutVerification(this.form.phone).then(
          (response) => {
            this.$store.dispatch("auth/setUserPhone", response.data.phone);
            this.isLoading = false;
            this.$snotify.success(this.$t(this.$getConst.pages.alerts.success_update));
          },
          (error) => {
            this.handleApiError(error);
            this.isLoading = false;
          }
        )

      },
      updateSms(checked) {
        this.isUpdatingSms = true;
        updateSms(this.form).then(
          (response) => {
            this.userContacts.sms = response.data.sms;
            this.$store.dispatch("auth/setUserSms", response.data.sms);
            this.isUpdatingSms = false;
          },
          (error) => {
            this.form.sms = !this.form.sms;
            this.isUpdatingSms = false;
            this.handleApiError(error);
          }
        )
      },
      getAllowedCountries() {
        this.isLoadingCountries = true;
        getAllowedMobileCountries().then(
          (response) => {
            this.allowedCountries = response.data.countries;
            this.isLoadingCountries = false;
          },
          (error) => {
            this.handleApiError(error);
            this.isLoadingCountries = false;
          }
        )
      }
    },
    created() {
      this.getAllowedCountries();
      this.userContacts = this.$store.getters["auth/userPersonalContacts"];
      this.getEditableData();
    },
  }
</script>

<style>
  @import '../../../assets/css/vue-phone-number-input.css';

  .input-tel__input {
    border-radius: 0 !important;
  }
</style>
